<template>
  <div class="return-container">
    <div class="return-content">
      <div v-if="isProcessing" class="processing">
        <div class="spinner"></div>
        <h2>正在處理您的付款結果...</h2>
        <p>請稍候，我們正在確認您的交易狀態</p>
      </div>
      
      <div v-else class="result">
        <div class="success-icon" v-if="status === 'SUCCESS'">✅</div>
        <div class="failed-icon" v-else-if="status === 'FAIL'">❌</div>
        <div class="cancel-icon" v-else-if="status === 'CANCEL'">⚠️</div>
        
        <h2 v-if="status === 'SUCCESS' && isSubscription">訂閱授權成功！</h2>
        <h2 v-else-if="status === 'SUCCESS'">付款成功！</h2>
        <h2 v-else-if="status === 'FAIL' && isSubscription">訂閱授權失敗</h2>
        <h2 v-else-if="status === 'FAIL'">付款失敗</h2>
        <h2 v-else-if="status === 'CANCEL' && isSubscription">訂閱授權已取消</h2>
        <h2 v-else-if="status === 'CANCEL'">交易已取消</h2>
        
        <p>{{ message }}</p>
        
        <div v-if="chargeId || subscriptionId" class="transaction-details">
          <h3 v-if="isSubscription">訂閱詳情</h3>
          <h3 v-else>交易詳情</h3>
          <div v-if="chargeId" class="detail-item">
            <span class="label">{{ isSubscription ? '授權序號：' : '交易序號：' }}</span>
            <span class="value">{{ chargeId }}</span>
          </div>
          <div v-if="subscriptionId" class="detail-item">
            <span class="label">訂閱編號：</span>
            <span class="value">{{ subscriptionId }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * NewebPay 支付返回頁面元件
 * 依據 window.newebpayReturnData 狀態，向 GHL 發送正確 postMessage 並顯示結果
 */
import { postToGhl, postToWindow } from '../utils/ghlMessaging';
import {
  buildSuccessResponsePayload,
  buildErrorResponsePayload,
  buildCloseResponsePayload,
} from '../utils/ghlEventPayloads';

export default {
  name: 'NewebPayReturn',
  data() {
    return {
      status: null,
      chargeId: '',
      message: '',
      isProcessing: true,
      isSubscription: false,
      subscriptionId: null
    };
  },
  mounted() {
    // 讀取 window.newebpayReturnData
    const data = window.newebpayReturnData || {};
    this.status = data.status || 'FAIL';
    this.chargeId = data.chargeId || '';
    this.message = data.message || '交易失敗，請稍後再試';
    this.isSubscription = data.isSubscription || false;
    this.subscriptionId = data.subscriptionId || null;

    console.log('NewebPayReturn result prepared:', {
      status: this.status, 
      hasChargeId: Boolean(this.chargeId),
      isSubscription: this.isSubscription,
      hasSubscriptionId: Boolean(this.subscriptionId),
    });

    // 封裝回傳函式：優先回傳給 opener（彈窗場景），否則回傳給 parent（iframe 場景）
    const sendToHost = (payload) => {
      if (window.opener && !window.opener.closed) {
        console.log('Posting payment result to opener:', payload.type);
        postToWindow(window.opener, payload);
      } else if (window.parent && window.parent !== window) {
        console.log('Posting payment result to parent:', payload.type);
        postToGhl(window.parent, payload);
      } else {
        console.warn('No opener/parent available to receive message');
      }
    };

    // 條件式 postMessage
    if (this.status === 'SUCCESS') {
      sendToHost(buildSuccessResponsePayload({
        chargeId: this.chargeId,
        subscriptionId: this.subscriptionId,
        isSubscription: this.isSubscription,
      }));
    } else if (this.status === 'FAIL') {
      const errorMessage = this.isSubscription ? '訂閱授權失敗，請稍後再試' : '交易失敗，請稍後再試';
      sendToHost(buildErrorResponsePayload({
        description: errorMessage,
        isSubscription: this.isSubscription,
      }));
    } else if (this.status === 'CANCEL') {
      sendToHost(buildCloseResponsePayload({ isSubscription: this.isSubscription }));
    }

    // 延長處理時間顯示，讓使用者能看到完整的處理畫面
    setTimeout(() => {
      this.isProcessing = false;
      
      // 強制關閉視窗邏輯 - 無論是否有 opener 都嘗試關閉
      try {
        // 對於成功的付款，強制嘗試關閉視窗
        if (this.status === 'SUCCESS') {
          if (window.opener && !window.opener.closed) {
            window.close();
          } else {
            window.close();
          }
          setTimeout(() => {
            window.close();
          }, 500);
        }
      } catch (e) {
        console.warn('window.close failed:', e);
      }
    }, 2000); // 延長到 2 秒
  }
};
</script>

<style scoped>
.return-container {
  min-height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.return-content {
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  text-align: center;
  max-width: 400px;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-height: auto;
}

.processing, .error, .result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  border: 4px solid #e3f2fd;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.processing h2, .result h2 {
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.processing p, .result p {
  animation: fadeInUp 0.8s ease-out 0.4s both;
}

.transaction-details {
  animation: fadeInUp 0.8s ease-out 0.6s both;
}

.success-icon, .failed-icon, .cancel-icon {
  font-size: 3.5rem;
  margin-bottom: 0.5rem;
}

.success-icon {
  background: linear-gradient(135deg, #4caf50, #45a049);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3));
}

.failed-icon {
  background: linear-gradient(135deg, #f44336, #d32f2f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 4px rgba(244, 67, 54, 0.3));
}

.cancel-icon {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 4px rgba(255, 152, 0, 0.3));
}

h2 {
  color: #1a202c;
  margin: 0 0 0.5rem 0;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

p {
  color: #718096;
  margin: 0 0 1.5rem 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.transaction-details {
  background: linear-gradient(145deg, #f7fafc, #edf2f7);
  padding: 2rem;
  border-radius: 16px;
  margin-top: 1.5rem;
  text-align: left;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
}

.transaction-details h3 {
  color: #2d3748;
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  letter-spacing: -0.01em;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.detail-item:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.9rem;
}

.value {
  color: #2d3748;
  font-weight: 500;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: #f7fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
}

.retry-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

@media (max-width: 768px) {
  .return-content {
    padding: 2rem;
    margin: 1rem;
  }
  
  h2 {
    font-size: 1.4rem;
  }
  
  .success-icon, .failed-icon, .cancel-icon {
    font-size: 3rem;
  }
}
</style>
